<template>
  <AuthLayout>
    <template #form>
      <!-- Loading State -->
      <LoadingScreen v-if="loading" color="var(--purple)" />

      <!-- Step 1 — enter the code the agent displayed -->
      <div v-else-if="step === 'code'" class="device-form">
        <AuthFormHeader
          title="Connect a device"
          subtitle="Enter the code shown by the app or agent you're connecting"
        />

        <form class="code-entry" @submit.prevent="resolveCode">
          <input
            ref="codeInput"
            :value="userCode"
            class="code-input"
            type="text"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            maxlength="9"
            placeholder="XXXX-XXXX"
            @input="onCodeInput"
          />
          <p v-if="resolveError" class="code-error">{{ resolveError }}</p>
          <an-button type="submit" variant="purple" size="lg" :disabled="!codeComplete || resolving" :rounded="false">
            {{ resolving ? 'Checking…' : 'Continue' }}
          </an-button>
        </form>
      </div>

      <!-- Step 2 — the same consent decisions as the interactive screen -->
      <div v-else-if="step === 'consent'" class="device-form">
        <AuthFormHeader title="Authorization Request" subtitle="A device is requesting access to your Anima account" />

        <div class="consent-info">
          <div class="info-item">
            <span class="info-label">Requesting</span>
            <span class="info-value">{{ clientName || 'Headless agent' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Code</span>
            <span class="info-value code-value">{{ formattedUserCode }}</span>
          </div>
        </div>

        <!-- Team Selection -->
        <div class="team-selection">
          <label class="team-label">Select a team</label>
          <an-select v-model="selectedTeamId" :options="teamOptions" placeholder="Select a team" />
        </div>

        <!-- Agent settings — optional; collapsed = connect with the list's own defaults,
             mirroring OAuthConsent.vue. -->
        <div class="agent-settings">
          <button type="button" class="agent-settings-toggle" @click="agentSettingsOpen = !agentSettingsOpen">
            <span>Agent settings <span class="toggle-optional">optional</span></span>
            <span class="toggle-chevron" :class="{ open: agentSettingsOpen }"></span>
          </button>

          <p v-if="!agentSettingsOpen" class="field-hint agent-settings-hint">
            Connecting as <strong>{{ clientName || 'this agent' }}</strong> with default access to your workspace —
            expand to name or restrict this agent.
          </p>

          <div v-if="agentSettingsOpen" class="agent-settings-body">
            <div class="agent-name-section">
              <label class="section-label">
                Agent name
                <span
                  class="info-tip"
                  tabindex="0"
                  data-tip="Naming this connection creates an agent you can scope and revoke independently. The same name reconnects to the same agent; a new name creates a separate one."
                  >i</span
                >
              </label>
              <input
                v-model="agentName"
                class="agent-name-input"
                type="text"
                maxlength="120"
                :placeholder="clientName || 'Agent name'"
              />
            </div>

            <div class="workspace-access">
              <label class="section-label">
                Workspace permissions
                <span class="section-count">({{ grantedCount }}/{{ workspaces.length }})</span>
                <span
                  class="info-tip"
                  tabindex="0"
                  data-tip="What this agent may do, workspace by workspace. It can never do more than you can in a workspace — and you can revoke it anytime."
                  >i</span
                >
              </label>

              <div v-if="workspacesLoading" class="workspace-loading">Loading…</div>
              <div v-else-if="workspacesError" class="workspace-error">{{ workspacesError }}</div>
              <WorkspacePermissions v-else v-model="grantedByWorkspace" :workspaces="workspaces" />
            </div>
          </div>
        </div>

        <!-- Unlike the interactive consent form (native POST → redirect), the
             device decision is submitted via XHR: the server replies with a
             JSON outcome and the agent learns the result by polling — there is
             no redirect target on this flow. -->
        <div class="consent-actions">
          <p v-if="submitError" class="code-error">{{ submitError }}</p>
          <an-button
            variant="purple"
            size="lg"
            :disabled="!canApprove || submitting"
            :rounded="false"
            @click="submitDecision('approve')"
          >
            {{ submitting ? 'Connecting…' : 'Approve' }}
          </an-button>
          <button type="button" class="deny-link" :disabled="submitting" @click="submitDecision('deny')">Deny</button>
        </div>

        <p class="consent-disclaimer">
          By approving, you allow this device to access your Anima account on behalf of the selected team.
        </p>
      </div>

      <!-- Step 3 — outcome; the agent picks it up on its next poll -->
      <div v-else class="device-form device-done">
        <template v-if="outcome === 'approved'">
          <AuthFormHeader
            title="Device connected"
            :subtitle="`${
              clientName || 'The agent'
            } is approved — you can return to your terminal. It will finish connecting on its own.`"
          />
        </template>
        <template v-else>
          <AuthFormHeader title="Request denied" subtitle="The device was not connected. You can close this page." />
        </template>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import AnSelect from '@/components/Select/Select';
import WorkspacePermissions from '@/components/OAuthConsent/WorkspacePermissions';
import auth from '@/auth';
import { readCookie } from '@/utils/cookie';

export default {
  name: 'OAuthDeviceVerify',
  components: {
    AuthLayout,
    AuthFormHeader,
    LoadingScreen,
    WorkspacePermissions,
    'an-select': AnSelect
  },
  data() {
    return {
      loading: true,
      step: 'code', // 'code' | 'consent' | 'done'
      userCode: '',
      resolving: false,
      resolveError: '',
      clientName: '',
      selectedTeamId: '',
      agentName: '',
      agentSettingsOpen: false,
      workspaces: [],
      offerableCapabilities: [],
      // Workspace id -> capabilities; a workspace absent from here gets no access.
      grantedByWorkspace: {},
      workspacesLoading: false,
      workspacesError: '',
      submitting: false,
      submitError: '',
      outcome: ''
    };
  },
  computed: {
    ...mapGetters('teamMemberships', ['userTeams']),
    teamOptions() {
      return this.userTeams.map((team) => ({
        label: team.name,
        value: team.id
      }));
    },
    // The server strips separators and case-folds; this is display formatting.
    formattedUserCode() {
      const raw = this.userCode.replace(/-/g, '');
      return raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
    },
    codeComplete() {
      return this.userCode.replace(/-/g, '').length === 8;
    },
    grantedWorkspaces() {
      return this.workspaces.filter((workspace) => (this.grantedByWorkspace[workspace.id] || []).length > 0);
    },
    grantedCount() {
      return this.grantedWorkspaces.length;
    },
    authorizationDetailsField() {
      // One entry per workspace (public-api schemas/authorizationDetails.ts).
      if (!this.grantedCount) return '';
      return JSON.stringify(
        this.grantedWorkspaces.map((workspace) => ({
          type: 'grids_capability',
          workspaceId: workspace.id,
          capabilities: this.grantedByWorkspace[workspace.id]
        }))
      );
    },
    canApprove() {
      if (!this.selectedTeamId) return false;
      return this.grantedCount > 0;
    }
  },
  watch: {
    selectedTeamId() {
      this.fetchConsentWorkspaces();
    }
  },
  async mounted() {
    if (!auth.isLoggedIn()) {
      const currentUrl = window.location.href;
      this.$router.push({
        name: 'login',
        query: { redirect: currentUrl }
      });
      return;
    }

    // verification_uri_complete pre-fills the code; auto-resolve it.
    const prefill = this.$route.query.user_code;
    if (prefill) {
      this.userCode = this.formatCode(String(prefill));
    }
    this.loading = false;
    if (this.codeComplete) {
      await this.resolveCode();
    }
  },
  methods: {
    ...mapActions('teamMemberships', ['fetchAllUserMemberships']),

    formatCode(raw) {
      const clean = raw
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 8);
      return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
    },

    onCodeInput(event) {
      this.userCode = this.formatCode(event.target.value);
      // Rewrite the input so stray characters never render.
      event.target.value = this.userCode;
      this.resolveError = '';
    },

    async resolveCode() {
      if (!this.codeComplete || this.resolving) return;
      this.resolving = true;
      this.resolveError = '';
      try {
        const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
        const response = await axios.get(`${baseUrl}/oauth/device-tx`, {
          params: { user_code: this.userCode },
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        this.clientName = response.data.client_name;

        await this.fetchAllUserMemberships({ id: 'me', skipCache: true });
        const currentTeamId = readCookie('anima_currentTeamId');
        if (currentTeamId && this.userTeams.some((t) => t.id === currentTeamId)) {
          this.selectedTeamId = currentTeamId;
        } else if (this.userTeams.length > 0) {
          this.selectedTeamId = this.userTeams[0].id;
        }
        // Setting selectedTeamId fires the watcher, which loads workspaces.

        this.step = 'consent';
      } catch (err) {
        console.error('[OAuth Device] Failed to resolve code:', err);
        this.resolveError =
          err.response?.status === 404
            ? 'This code is invalid, expired, or already used. Check it, or ask the agent for a new one.'
            : 'Could not verify the code. Please try again.';
      } finally {
        this.resolving = false;
      }
    },

    async fetchConsentWorkspaces() {
      if (!this.selectedTeamId) return;
      this.workspacesLoading = true;
      this.workspacesError = '';
      try {
        const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
        const response = await axios.get(`${baseUrl}/v1/grids/consent-workspaces`, {
          params: { team_id: this.selectedTeamId },
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        this.workspaces = response.data.workspaces || [];
        this.offerableCapabilities = response.data.offerableCapabilities || [];
        // One-click approve: General with everything the human may delegate there; every other
        // workspace is added deliberately.
        const general = this.workspaces.find((workspace) => workspace.isGeneral) || this.workspaces[0];
        this.grantedByWorkspace = general ? { [general.id]: [...(general.offerableCapabilities || [])] } : {};
      } catch (err) {
        console.error('[OAuth Device] Failed to load workspaces:', err);
        this.workspaces = [];
        this.grantedByWorkspace = {};
        this.workspacesError = 'Could not load workspaces for this team.';
      } finally {
        this.workspacesLoading = false;
      }
    },

    async submitDecision(action) {
      if (this.submitting) return;
      this.submitting = true;
      this.submitError = '';
      try {
        const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
        const body = new URLSearchParams({
          user_code: this.userCode,
          action,
          team_id: this.selectedTeamId,
          agent_name: this.agentName || this.clientName,
          authorization_details: this.authorizationDetailsField
        });
        const response = await axios.post(`${baseUrl}/oauth/consent`, body, {
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        this.outcome = response.data.status;
        this.step = 'done';
      } catch (err) {
        console.error('[OAuth Device] Consent submit failed:', err);
        // A clamp rejection leaves the flow intact — the human can correct the
        // selection and submit again.
        this.submitError = err.response?.data?.error_description || 'Could not submit your decision. Please try again.';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

// Same top-anchoring as the consent screen (the shared auth layout centers
// vertically, which reads as excessive top padding on taller views).
::v-deep .auth-page-wrapper {
  align-items: flex-start;
  padding-top: 32px;
}

.device-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  > :first-child {
    margin-bottom: 0;
  }
}

.code-entry {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.code-input {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white);
  height: 56px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.35em;
  text-align: center;
  text-transform: uppercase;

  &::placeholder {
    color: var(--transparent-white-50);
    letter-spacing: 0.35em;
  }

  &:focus {
    outline: none;
    border-color: var(--purple);
  }
}

.code-error {
  color: #ffb74d;
  font-size: 13px;
  margin: 0;
  text-align: center;
}

.consent-info {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-sm;
  padding: $auth-gap-md;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .info-label {
    color: var(--transparent-white-50);
    font-size: 14px;
  }

  .info-value {
    color: var(--white);
    font-size: 14px;
    font-weight: 500;

    &.code-value {
      font-weight: 600;
      letter-spacing: 0.15em;
    }
  }
}

.team-selection {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .team-label {
    color: var(--transparent-white-70);
    font-size: 14px;
    font-weight: 500;
  }

  .an-select {
    ::v-deep .selected {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: var(--white);
      height: 48px;
      border-radius: 6px;

      &.placeholder {
        color: var(--transparent-white-50);
      }

      &::after {
        filter: invert(1);
      }
    }

    ::v-deep .options {
      background: var(--dark-card-background);
      border-color: rgba(255, 255, 255, 0.1);
      color: var(--white);

      .option {
        color: var(--white);

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }
  }
}

.consent-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 4px;

  .an-button {
    width: 100%;
  }

  .deny-link {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    color: var(--transparent-white-50);
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: var(--transparent-white-70);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}

.agent-settings {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}

.agent-settings-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  padding: 0 0 0 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 1px solid rgba(255, 255, 255, 0.3);
    outline-offset: -1px;
    border-radius: 6px;
  }

  .toggle-chevron {
    display: inline-block;
    width: 32px;
    height: 32px;
    background-image: url('~@/assets/svg/chevron-down.svg');
    background-repeat: no-repeat;
    background-position: center;
    filter: invert(1);
    transition: transform 0.15s ease;

    &.open {
      transform: rotate(180deg);
    }
  }

  .toggle-optional {
    color: rgba(255, 255, 255, 0.4);
    font-size: 11px;
    font-weight: 400;
    margin-left: 6px;
  }
}

.agent-settings-hint {
  padding: 0 16px 12px;
}

.agent-settings-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 16px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
}

.info-tip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.55);
  font-size: 9px;
  font-style: italic;
  font-family: Georgia, serif;
  cursor: help;
  user-select: none;

  &:focus {
    outline: none;
  }

  &:hover::after,
  &:focus::after {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 6px);
    left: -4px;
    background: #2b2b30;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    padding: 6px 10px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 11px;
    font-style: normal;
    font-family: $auth-body-font;
    font-weight: 400;
    line-height: 1.4;
    white-space: normal;
    width: max-content;
    max-width: 240px;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    pointer-events: none;
  }
}

.agent-name-section,
.workspace-access {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.agent-name-input {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white);
  height: 48px;
  border-radius: 6px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 14px;

  &::placeholder {
    color: var(--transparent-white-50);
  }

  &:focus {
    outline: none;
    border-color: var(--purple);
  }
}

.field-hint {
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  line-height: 1.5;
  margin: 0;

  strong {
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
  }
}

.workspace-loading,
.workspace-error {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  padding: 8px 0;
}

.workspace-error {
  color: #ffb74d;
}

.consent-disclaimer {
  text-align: center;
  color: var(--transparent-white-50);
  font-size: 12px;
  line-height: 1.5;
}

.device-done {
  text-align: center;
}
</style>
